import type { ReviewRepository } from '../repository/review.repository';
import type { Review, ReviewStats } from './review.types';

export type { Review, ReviewStats } from './review.types';
export type { ReviewVote } from './review.types';

/**
 * Application API for storefront reviews (delegates to {@link ReviewRepository}).
 */
export class ReviewService {
  constructor(private readonly reviews: ReviewRepository) {}

  createReview(reviewData: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>) {
    return this.reviews.createReview(reviewData);
  }

  updateReview(reviewId: string, updates: Partial<Review>) {
    return this.reviews.updateReview(reviewId, updates);
  }

  deleteReview(reviewId: string) {
    return this.reviews.deleteReview(reviewId);
  }

  getTargetReviews(
    targetId: string,
    targetType: string,
    options?: {
      status?: string;
      limit?: number;
      orderBy?: 'newest' | 'oldest' | 'rating' | 'helpful';
    },
  ) {
    return this.reviews.getTargetReviews(targetId, targetType, options);
  }

  getUserReviews(userId: string) {
    return this.reviews.getUserReviews(userId);
  }

  voteOnReview(reviewId: string, userId: string, voteType: 'helpful' | 'unhelpful') {
    return this.reviews.voteOnReview(reviewId, userId, voteType);
  }

  getTargetStats(targetId: string, targetType: string): Promise<ReviewStats> {
    return this.reviews.getTargetStats(targetId, targetType);
  }

  moderateReview(reviewId: string, status: 'approved' | 'rejected', adminNotes?: string) {
    return this.reviews.moderateReview(reviewId, status, adminNotes);
  }

  flagReview(reviewId: string, reason: string) {
    return this.reviews.flagReview(reviewId, reason);
  }
}
